import { useEffect, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  ["software", "Software/Tools"], ["rent", "Miete"], ["personnel", "Personal"],
  ["tax_advisor", "Steuerberater"], ["insurance", "Versicherung"], ["hosting", "Hosting"],
  ["other_fixed", "Sonstige Fixkosten"],
];
const INTERVALS = [["monthly", "Monatlich"], ["quarterly", "Quartalsweise"], ["yearly", "Jährlich"]];

const empty = {
  name: "", category: "software", amount_net: 0, vat_rate: 19, vat_included: false,
  due_day: 1, interval: "monthly", start_date: "", end_date: "", active: true, notes: "",
};

function normalizedMonthly(fc) {
  const gross = fc.vat_included ? fc.amount_net : fc.amount_net * (1 + (fc.vat_rate || 0) / 100);
  if (fc.interval === "monthly") return gross;
  if (fc.interval === "quarterly") return gross / 3;
  return gross / 12;
}

export default function FixedCostsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => setItems((await api.get("/fixed-costs")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      ...form,
      amount_net: Number(form.amount_net), vat_rate: Number(form.vat_rate),
      due_day: Number(form.due_day),
      start_date: form.start_date || null, end_date: form.end_date || null,
    };
    try {
      if (editing) await api.patch(`/fixed-costs/${editing.id}`, payload);
      else await api.post("/fixed-costs", payload);
      toast.success(editing ? "Aktualisiert" : "Angelegt");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const del = async (id) => {
    if (!window.confirm("Fixkosten löschen?")) return;
    await api.delete(`/fixed-costs/${id}`); toast.success("Gelöscht"); load();
  };

  const monthly = items.filter(i => i.active).reduce((s, i) => s + normalizedMonthly(i), 0);

  return (
    <div className="space-y-6" data-testid="fixed-costs-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Fixkosten</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Wiederkehrende Kosten</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild><Button data-testid="add-fixed-cost-btn"><Plus className="h-4 w-4 mr-2" />Neue Fixkosten</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "Bearbeiten" : "Neue Fixkosten"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fc-name" /></div>
              <div>
                <Label>Kategorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Intervall</Label>
                <Select value={form.interval} onValueChange={(v) => setForm({ ...form, interval: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{INTERVALS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Betrag (€)</Label><Input type="number" step="0.01" value={form.amount_net} onChange={(e) => setForm({ ...form, amount_net: e.target.value })} data-testid="fc-amount" /></div>
              <div><Label>USt %</Label><Input type="number" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} /></div>
              <div className="col-span-2 flex items-center justify-between border rounded-lg px-4 py-2">
                <Label className="cursor-pointer m-0">Betrag enthält USt</Label>
                <Switch checked={form.vat_included} onCheckedChange={(v) => setForm({ ...form, vat_included: v })} />
              </div>
              <div><Label>Fälligkeitstag</Label><Input type="number" min="1" max="31" value={form.due_day} onChange={(e) => setForm({ ...form, due_day: e.target.value })} /></div>
              <div className="flex items-center justify-between border rounded-lg px-4 py-2">
                <Label className="cursor-pointer m-0">Aktiv</Label>
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              </div>
              <div className="col-span-2"><Label>Notiz</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="fc-save">{editing ? "Speichern" : "Anlegen"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Monatlich gesamt" value={fmtCurrency(monthly)} />
        <Stat label="Quartal" value={fmtCurrency(monthly * 3)} />
        <Stat label="Jahr" value={fmtCurrency(monthly * 12)} />
        <Stat label="Aktive Positionen" value={items.filter(i => i.active).length} />
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white">
        <Table data-testid="fixed-costs-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Kategorie</TableHead><TableHead>Intervall</TableHead>
              <TableHead>Fällig am</TableHead><TableHead className="text-right">Betrag (Brutto)</TableHead>
              <TableHead className="text-right">Monatlich</TableHead><TableHead>Aktiv</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((fc) => (
              <TableRow key={fc.id} className="table-row-hover" data-testid={`fc-row-${fc.id}`}>
                <TableCell className="font-medium">{fc.name}</TableCell>
                <TableCell className="text-sm">{CATEGORIES.find(c => c[0] === fc.category)?.[1] || fc.category}</TableCell>
                <TableCell className="text-sm">{INTERVALS.find(i => i[0] === fc.interval)?.[1]}</TableCell>
                <TableCell className="text-sm">Tag {fc.due_day}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCurrency(fc.vat_included ? fc.amount_net : fc.amount_net * (1 + (fc.vat_rate || 0) / 100))}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCurrency(normalizedMonthly(fc))}</TableCell>
                <TableCell><span className={`dot ${fc.active ? "" : "opacity-30"}`} style={{ backgroundColor: fc.active ? "#10B981" : "#A3A3A3" }} /></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(fc); setForm({ ...empty, ...fc, start_date: fc.start_date || "", end_date: fc.end_date || "" }); setOpen(true); }} data-testid={`fc-edit-${fc.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(fc.id)} data-testid={`fc-delete-${fc.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">{label}</div>
      <div className="font-display text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
